// @ts-nocheck
/*
 * Port of voice-library/View-voice-files.js (682 lines). BYTE-IDENTICAL.
 * Prompt list scoped by role: SUPERADMIN sees all approved prompts
 * (`prompt_status=1`); other users see their own (`user_id=<uid>`).
 * View-Files modal expands the `prompt_url` array (per-language voice
 * files) into a stat-per-row table with listen/download and inline audio
 * player. Edit action only shows when the prompt has â‰¥1 rejected or
 * pending language file â€” routes to the IVR EDIT_VOICE page.
 * PrimeReact `DataTable`/`Column`/`Checkbox`/`Dialog` â†’ obd3-gui DataTable
 * + tanstack ColumnDef, native <input type="checkbox">, and react-bootstrap
 * `Modal` for the files viewer. DeleteIcon inlined.
 */
import React, { useState, useEffect } from 'react';
import { storageUrlOr } from '@/utilities/storageUrl';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ fluid, className, children, ...props }: any) => (
  <div className={_cx(fluid ? 'container-fluid' : 'container', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
Card.Text = ({ className, children, ...props }: any) => (
  <p className={_cx('card-text', className)} {...props}>
    {children}
  </p>
);
Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const _TabsCtx = React.createContext<any>({});
const Tabs: any = ({
  activeKey,
  defaultActiveKey,
  onSelect,
  children,
  className,
  ...props
}: any) => {
  const [internalKey, setInternalKey] = React.useState(defaultActiveKey || null);
  const current = activeKey !== undefined ? activeKey : internalKey;
  const handle = (k: any) => {
    if (activeKey === undefined) setInternalKey(k);
    onSelect?.(k);
  };
  const tabs = React.Children.toArray(children).filter(Boolean) as any[];
  if (current === null && tabs.length) {
    setInternalKey(tabs[0]?.props?.eventKey);
  }
  return (
    <_TabsCtx.Provider value={{ current, setKey: handle }}>
      <div className={className} {...props}>
        <ul className="nav nav-tabs" role="tablist">
          {tabs.map((t: any) => (
            <li key={t.props.eventKey} className="nav-item" role="presentation">
              <button
                type="button"
                className={_cx('nav-link', current === t.props.eventKey && 'active')}
                onClick={() => handle(t.props.eventKey)}
                disabled={t.props.disabled}
              >
                {t.props.title}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {tabs.map((t: any) => (
            <div
              key={t.props.eventKey}
              className={_cx('tab-pane', current === t.props.eventKey && 'active show')}
              style={{ display: current === t.props.eventKey ? undefined : 'none' }}
            >
              {t.props.children}
            </div>
          ))}
        </div>
      </div>
    </_TabsCtx.Provider>
  );
};
const Tab: any = () => null;
const Tooltip: any = ({ children, id: _id, ...props }: any) => (
  <span {...props}>{children}</span>
);
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { getApiCall, postApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import {
  USER_ROLES,
  MODULE_ACL,
  ROUTES,
} from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as PageButton } from '@/components/ui/button';
import { Check,
  Download,
  Plus,
  RefreshCw, Trash2,
  Eye,
  Pencil,
  X,
  Play,
  Square,
  Layers,
} from 'lucide-react';
import { getUser } from '@/utilities/setting';
import { canUploadPromptsOrMoh } from '@/utilities/promptUploadAccess';
import { fetchAuthedBlobUrl, revokeAuthedBlobUrl } from '@/utilities/mediaAudio';
import './index.css';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const API_BASE = '/v2';

const DeleteIcon = ({ handleCallback }: any) => (
  <Link
    className="btn btn-sm btn-icon btn-danger"
    title="Delete"
    to="#"
    onClick={(e) => {
      e.preventDefault();
      handleCallback();
    }}
  >
    <span className="btn-inner">
      <svg
        width="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        stroke="currentColor"
      >
        <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  </Link>
);

function getFirstUrl(prompt_url: any) {
  if (!prompt_url) return '';
  if (typeof prompt_url === 'string') return prompt_url;
  if (Array.isArray(prompt_url) && prompt_url.length > 0) return prompt_url[0]?.url || '';
  return '';
}

function getPromptApprovedAt(rowData: any) {
  const entries = Array.isArray(rowData?.prompt_url) ? rowData.prompt_url : [];
  const approvedTimes = entries
    .map((e: any) => e?.approved_at)
    .filter(Boolean)
    .map((d: any) => new Date(d).getTime())
    .filter((t: number) => !Number.isNaN(t));
  if (approvedTimes.length > 0) return new Date(Math.max(...approvedTimes));
  return rowData?.approval_time ? new Date(rowData.approval_time) : null;
}

const ViewVoiceFiles = () => {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userId, setUserId] = useState<any>(null);
  const [filesModal, setFilesModal] = useState<any>({
    visible: false,
    entries: [],
    promptName: '',
  });
  const [playingUrl, setPlayingUrl] = useState('');
  const [selectedData, setSelectedPrompt] = useState<any[]>([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [deletePromptId, setDeletePromptId] = useState<any>(undefined);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<any>(null);
  const [originalDataList, setOriginalDataList] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<any>(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});

  const fetchPrompts = () => {
    setLoading(true);
    let url = '';

    if (isSuperAdmin) {
      url = `${API_BASE}/prompt?prompt_status=1`;
    } else if (userId) {
      url = `${API_BASE}/prompt/?user_id=${userId}`;
    }

    if (url) {
      getApiCall(url)
        .then(onPromptsSuccess)
        .catch(onError)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchValue) {
      setPrompts(
        prompts.filter((data: any) =>
          data.prompt_name.toLowerCase().includes(searchValue.trim().toLowerCase()),
        ),
      );
    } else {
      setPrompts(originalDataList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, prompts]);

  useEffect(() => {
    fetchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, userId]);

  const handleCheckboxToggle = (promptId: any) => {
    const isSelected = selectedData.includes(promptId);
    setSelectedPrompt(
      isSelected
        ? selectedData.filter((id: any) => id !== promptId)
        : [...selectedData, promptId],
    );
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };

  function handlePromptDelete(id: any) {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeletePromptId(id);
  }

  const handleSelectAll = () => {
    if (selectedData.length === prompts.length) {
      setSelectedPrompt([]);
    } else {
      const allPromptsIds = prompts.map((prompt: any) => prompt.id);
      setSelectedPrompt(allPromptsIds);
    }
  };

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    if (confirmDelete) {
      postApiCall(`${API_BASE}/prompt/delete`, {
        promptIds: isMultiDelete ? selectedData : [deletePromptId],
      })
        .then(onDeleteSuccess)
        .catch(onDeleteFail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete]);

  function onDeleteSuccess(resp: any) {
    toast.success(resp);
    let updatedList: any[];
    if (isMultiDelete) {
      updatedList = prompts
        .filter((val: any) => !selectedData.includes(val.id))
        .map((val: any, key: number) => {
          val['key'] = key + 1;
          return val;
        });
      setSelectedPrompt([]);
    } else {
      updatedList = prompts
        .filter((val: any) => val.id != deletePromptId)
        .map((val: any, key: number) => {
          val['key'] = key + 1;
          return val;
        });
    }
    setPrompts(updatedList);
    setEnableDeleteModal(false);
    setDeletePromptId(undefined);
    setConfirmDelete(false);
    fetchPrompts();
  }

  const handleRefresh = () => {
    setLoading(true);
    setSearchValue('');
    fetchPrompts();
  };

  function onDeleteFail(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.warning(`Unable to delete prompt: ${err}`);
    }
    setConfirmDelete(false);
  }

  useEffect(() => {
    if (searchValue) {
      const filtered = originalDataList.filter((data: any) =>
        data.prompt_name.toLowerCase().includes(searchValue.trim().toLowerCase()),
      );
      setPrompts(filtered);
    } else {
      setPrompts(originalDataList);
    }
  }, [searchValue, originalDataList]);

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
      }
      if (user.role == USER_ROLES.SUPERADMIN) {
        setIsSuperAdmin(true);
        setUserId(null);
        setUserRole(user.role);
      } else {
        setIsSuperAdmin(false);
        setUserId(user.id);
        setUserRole(user.role);
      }
    }
  }, []);

  const onPromptsSuccess = (resp: any) => {
    const sortedResp = [...resp].sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const updatedList = sortedResp.map((item: any, index: number) => ({
      ...item,
      key: index + 1,
    }));
    setPrompts(updatedList);
    setOriginalDataList(updatedList);
  };

  const onError = (error: any) => {
    toast.warning(`Failed to load prompts: ${error}`);
    setShowToast(true);
  };

  const downloadFile = async (fileUrl: string, filename?: string) => {
    try {
      const { objectUrl: url } = await fetchAuthedBlobUrl(fileUrl);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || fileUrl.split('/').pop() || 'prompt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      revokeAuthedBlobUrl(url);
    } catch (error) {
      console.error('Error while downloading the file:', error);
      toast.error('Unable to download the file');
    }
  };

  const openFilesModal = (rowData: any) => {
    const raw = Array.isArray(rowData.prompt_url)
      ? rowData.prompt_url
      : rowData.prompt_url
      ? [
          {
            language: 'hi',
            url: rowData.prompt_url,
            duration: rowData.prompt_duration || 0,
            status: rowData.prompt_status || 0,
          },
        ]
      : [];
    const entries = raw.map((entry: any) => ({
      ...entry,
      url: storageUrlOr('prompt', entry?.blob_path, entry?.url),
    }));
    setPlayingUrl('');
    setFilesModal({ visible: true, entries, promptName: rowData.prompt_name });
  };

  const closeFilesModal = () => {
    setFilesModal({ visible: false, entries: [], promptName: '' });
    setPlayingUrl('');
  };

  const hasRejectedLanguageFile = (rowData: any) => {
    const entries = Array.isArray(rowData?.prompt_url) ? rowData.prompt_url : [];
    return entries.some(
      (entry: any) => Number(entry?.status) === 2 || Number(entry?.status) === 0,
    );
  };

  const actionBodyTemplate = (rowData: any) => {
    const isDeleted =
      rowData?.is_deleted === true ||
      Number(rowData?.is_deleted) === 1 ||
      Boolean(rowData?.deleted_at);

    return (
      <div className="d-flex align-items-center gap-2">
        <button
          onClick={() => openFilesModal(rowData)}
          title="View Files"
          style={{
            border: 'none',
            background: 'none',
            padding: '0 4px',
            cursor: 'pointer',
          }}
        >
          <Eye style={{ color: '#6c757d', width: 20, height: 20 }} />
        </button>

        {(isSuperAdmin || loggedInUserACL[MODULE_ACL.VOICE_LIBRARY]?.edit) &&
          !isDeleted &&
          hasRejectedLanguageFile(rowData) && (
            <button
              onClick={() =>
                navigate(`/${ROUTES.IVR.EDIT_VOICE.replace(':id', rowData.id)}`, {
                  state: { prompt: rowData },
                })
              }
              title="Edit Rejected or Pending File"
              style={{
                border: 'none',
                background: 'none',
                padding: '0 4px',
                cursor: 'pointer',
              }}
            >
              <Pencil style={{ color: '#f0ad4e', width: 18, height: 18 }} />
            </button>
          )}

        {(isSuperAdmin || loggedInUserACL[MODULE_ACL.VOICE_LIBRARY]?.delete) &&
          !isDeleted && (
            <DeleteIcon handleCallback={() => handlePromptDelete(rowData?.id)} />
          )}
      </div>
    );
  };

  const handleExport = async () => {
    try {
      exportToCSV({
        fileName: 'voice-files-list',
        data: prompts,
        columns: [
          { header: 'Name', accessor: (row: any) => row.prompt_name || '-' },
          {
            header: 'Languages',
            accessor: (row: any) => {
              if (!row.prompt_url) return '-';
              if (typeof row.prompt_url === 'string') return row.prompt_url;
              return row.prompt_url.map((e: any) => e.language || '-').join(', ');
            },
          },
          {
            header: 'Duration',
            accessor: (row: any) => row.prompt_duration || '-',
          },
          {
            header: 'Category',
            accessor: (row: any) => row.prompt_category || '-',
          },
          {
            header: 'Voice Type',
            accessor: (row: any) => {
              const vt = String(row.voice_type);
              if (vt === '1') return 'Incoming';
              if (vt === '0') return 'Outgoing';
              if (vt === '2') return 'Both';
              return '-';
            },
          },
          ...(userRole !== USER_ROLES.CALL_CENTRE_ADMIN
            ? [
                {
                  header: 'Deleted By',
                  accessor: (row: any) => row.deletedBy?.name || '-',
                },
                {
                  header: 'Deleted At',
                  accessor: (row: any) =>
                    row.deleted_at
                      ? new Date(row.deleted_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })
                      : '-',
                },
              ]
            : []),
          {
            header: 'Status',
            accessor: (row: any) => {
              if (row.prompt_status == 0) return 'Pending';
              if (row.prompt_status == 1) return 'Approved';
              if (row.prompt_status == 2) return 'Rejected';
              return '-';
            },
          },
        ],
      });
    } finally {
      // no-op
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Voice Library',
    'read',
  );

  if (
    Object.keys(moduleOperationsForReadPermissionCheck).length > 0 &&
    !canRead &&
    !isSuperAdmin
  ) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={selectedData.length === prompts.length && prompts.length > 0}
          onChange={handleSelectAll}
        />
      ),
      enableSorting: false,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedData.includes(row.original.id)}
          onChange={() => handleCheckboxToggle(row.original.id)}
        />
      ),
    },
    { accessorKey: 'prompt_name', header: 'Name' },
    { accessorKey: 'prompt_duration', header: 'Total Duration' },
    {
      id: 'creator',
      header: 'Created By',
      cell: ({ row }: any) =>
        row.original?.creator?.name || row.original?.creator?.username || '-',
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }: any) => {
        if (!row.original.created_at) return '-';
        return new Date(row.original.created_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      id: 'approvedAt',
      header: 'Approved At',
      cell: ({ row }: any) => {
        const approvedAt = getPromptApprovedAt(row.original);
        if (!approvedAt) return '-';
        return approvedAt.toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      id: 'voice_type',
      header: 'Voice Type',
      cell: ({ row }: any) => {
        const vt = String(row.original.voice_type);
        if (vt === '1') return 'Incoming';
        if (vt === '0') return 'Outgoing';
        if (vt === '2') return 'Both';
        return '-';
      },
    },
    ...(userRole !== USER_ROLES.CALL_CENTRE_ADMIN
      ? [
          {
            id: 'deletedBy',
            header: 'Deleted By',
            cell: ({ row }: any) => row.original?.deletedBy?.name || '-',
          } as ColumnDef<any>,
          {
            id: 'deleted_at',
            header: 'Deleted At',
            cell: ({ row }: any) => {
              if (!row.original.deleted_at) return '-';
              return new Date(row.original.deleted_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              });
            },
          } as ColumnDef<any>,
        ]
      : []),
    {
      id: 'action',
      header: 'Actions',
      enableSorting: false,
      // Never let this be toggled off via the Columns dropdown — a
      // persisted "hidden" pref (localStorage, keyed by tableId) would
      // otherwise permanently hide View/Edit for that browser.
      enableHiding: false,
      cell: ({ row }: any) => actionBodyTemplate(row.original),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Voice List"
        description="Voice files uploaded for playback in IVR and outbound campaigns."
        actions={
          <>
            <PageButton
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </PageButton>
            {prompts.length > 0 && (
              <PageButton onClick={handleExport} variant="outline">
                <Download className="h-4 w-4" />
                Export
              </PageButton>
            )}
            {(isSuperAdmin || loggedInUserACL?.[MODULE_ACL.VOICE_LIBRARY]?.add) && (
              <PageButton onClick={() => navigate(`/${ROUTES.IVR.BULK_ADD_VOICE}`)}>
                <Layers className="h-4 w-4" />
                Bulk Upload
              </PageButton>
            )}
            {canUploadPromptsOrMoh() &&
              (isSuperAdmin || loggedInUserACL?.[MODULE_ACL.VOICE_LIBRARY]?.add) && (
              <PageButton onClick={() => navigate(`/${ROUTES.IVR.ADD_VOICE}`)}>
                <Plus className="h-4 w-4" />
                Add Voice File
              </PageButton>
            )}
          </>
        }
      />

      {selectedData.length > 0 &&
        (isSuperAdmin ||
          loggedInUserACL?.[MODULE_ACL.VOICE_LIBRARY]?.delete) && (
          <div
            className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/[0.06] px-3 py-2"
            style={{
              boxShadow:
                '4px 4px 10px rgba(163, 177, 198, 0.28), -4px -4px 10px rgba(255, 255, 255, 0.85)',
            }}
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
              {selectedData.length} selected
            </span>
            <button
              type="button"
              onClick={handleMultiDelete}
              className="inline-flex items-center gap-1.5 rounded-md bg-rose-100 px-2.5 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedPrompt([])}
              className="ml-auto text-[11px] font-semibold text-muted-foreground transition hover:text-foreground"
            >
              Clear
            </button>
          </div>
        )}

      <DataTable
        tableId="voice-files"
        columns={columns}
        data={prompts}
        pageSize={10}
        emptyTitle="No voice files"
        disableExport
      />

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
        clearSelectedData={() => setSelectedPrompt([])}
      />

      <Modal
        show={filesModal.visible}
        onHide={closeFilesModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Voice Files - {filesModal.promptName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              color: '#334155',
            }}
          >
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                {[
                  'Language',
                  'File Name',
                  'Duration',
                  'Status',
                  'Action Time',
                  'Listen / Download',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '11px 14px',
                      textAlign: h === 'Listen / Download' ? 'center' : 'left',
                      fontWeight: 600,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#64748b',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filesModal.entries.map((entry: any, i: number) => (
                <tr
                  key={i}
                  style={{
                    borderBottom: '1px solid #e2e8f0',
                    background:
                      playingUrl === entry.url
                        ? '#eff6ff'
                        : i % 2 === 0
                        ? '#fff'
                        : '#f8fafc',
                    transition: 'background 0.15s',
                  }}
                >
                  <td style={{ padding: '12px 14px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        background: '#dbeafe',
                        color: '#1d4ed8',
                        border: '1px solid #bfdbfe',
                      }}
                    >
                      {entry.language || '-'}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '12px 14px',
                      fontSize: '13px',
                      color: '#475569',
                      fontFamily: 'monospace',
                    }}
                  >
                    {entry.name || filesModal.promptName || '-'}
                  </td>
                  <td style={{ padding: '12px 14px', fontWeight: 500 }}>
                    {entry.duration ? `${entry.duration}s` : '-'}
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background:
                          entry.status == 1
                            ? '#dcfce7'
                            : entry.status == 2
                            ? '#fee2e2'
                            : '#fef9c3',
                        color:
                          entry.status == 1
                            ? '#15803d'
                            : entry.status == 2
                            ? '#b91c1c'
                            : '#a16207',
                        border: `1px solid ${
                          entry.status == 1
                            ? '#bbf7d0'
                            : entry.status == 2
                            ? '#fecaca'
                            : '#fde68a'
                        }`,
                      }}
                    >
                      {entry.status == 1
                        ? 'Approved'
                        : entry.status == 2
                        ? 'Rejected'
                        : 'Pending'}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '12px 14px',
                      fontSize: '12px',
                      color: '#64748b',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {entry.approved_at ? (
                      <span title={new Date(entry.approved_at).toLocaleString('en-IN')}>
                        <Check
                          style={{
                            color: '#10b981',
                            marginRight: 4,
                            width: 13,
                            height: 13,
                            display: 'inline',
                            verticalAlign: '-2px',
                          }}
                        />
                        {new Date(entry.approved_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    ) : entry.rejected_at ? (
                      <span title={new Date(entry.rejected_at).toLocaleString('en-IN')}>
                        <X
                          style={{
                            color: '#f43f5e',
                            marginRight: 4,
                            width: 13,
                            height: 13,
                            display: 'inline',
                            verticalAlign: '-2px',
                          }}
                        />
                        {new Date(entry.rejected_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 14,
                      }}
                    >
                      <button
                        onClick={() =>
                          setPlayingUrl(playingUrl === entry.url ? '' : entry.url)
                        }
                        title={playingUrl === entry.url ? 'Stop' : 'Listen'}
                        style={{
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        {playingUrl === entry.url ? (
                          <Square
                            style={{ width: 17, height: 17, color: '#ef4444' }}
                          />
                        ) : (
                          <Play style={{ width: 17, height: 17, color: '#3b82f6' }} />
                        )}
                      </button>
                      <button
                        onClick={() =>
                          downloadFile(
                            entry.url,
                            `${filesModal.promptName}_${entry.language}`,
                          )
                        }
                        title="Download"
                        style={{
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        <Download style={{ width: 17, height: 17, color: '#10b981' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {playingUrl && (
            <div
              style={{
                marginTop: '16px',
                padding: '14px 16px',
                background: '#f1f5f9',
                borderRadius: 10,
                border: '1px solid #e2e8f0',
              }}
            >
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Now Playing
              </p>
              <audio
                key={playingUrl}
                controls
                autoPlay
                style={{ width: '100%', borderRadius: 6 }}
              >
                <source src={playingUrl} type="audio/mpeg" />
              </audio>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </FillPage>
  );
};

export default ViewVoiceFiles;
